import React, { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import {
  AlertCircle,
  Briefcase,
  LogIn,
  XCircle,
  CheckCircle2,
  Loader2,
  Mail,
  ArrowRight,
  type LucideIcon,
} from 'lucide-react';
import { Button } from 'components/ui/button';
import { LogoMark } from 'components/global/LogoMark';
import { useForceLanguage } from 'hooks/use-force-language';
import { DEFAULT_LANGUAGE } from 'i18n';
import { cn } from 'lib/utils';
import * as Organization from 'modules/organization';
import { useTranslation } from 'react-i18next';
import * as AuthModule from 'modules/auth';

interface InviteInfo {
  email: string;
  role: string;
  organization: { id: string; name: string; slug: string };
  userExists: boolean;
}

/** "AUTHOR" → "Author", "ORG_ADMIN" → "Org admin". */
const roleLabel = (role?: string) =>
  role ? role.charAt(0).toUpperCase() + role.slice(1).toLowerCase().replace(/_/g, ' ') : '';

const initials = (name?: string) =>
  (name || '?')
    .split(/\s+/)
    .map((w) => w[0])
    .filter(Boolean)
    .slice(0, 2)
    .join('')
    .toUpperCase();

/** Centered invite card with the brand top bar (+ optional avatar). */
function InviteShell({ children, avatar }: { children: React.ReactNode; avatar?: React.ReactNode }) {
  useForceLanguage(DEFAULT_LANGUAGE);
  return (
    <div
      data-surface="studio"
      className="flex min-h-screen items-center justify-center bg-muted/30 px-4 py-10 text-foreground"
    >
      <div className="w-full max-w-md">
        <div className="overflow-hidden rounded-lg border border-border bg-background shadow-sm">
          <div className="flex items-center justify-between border-b border-border px-6 py-4">
            <div className="flex items-center gap-2">
              <LogoMark size={24} />
              <span className="font-display text-sm font-extrabold tracking-[-0.02em]">Examing</span>
            </div>
            {avatar}
          </div>
          <div className="px-6 py-8 text-center">{children}</div>
        </div>
      </div>
    </div>
  );
}

/** A 48px grey circle; `tone` colours the glyph only (red for expired, green for joined). */
function IconTile({ Icon, tone }: { Icon: LucideIcon; tone: string }) {
  return (
    <div className="mx-auto mb-5 flex h-12 w-12 items-center justify-center rounded-full bg-muted">
      <Icon className={cn('h-6 w-6', tone)} aria-hidden />
    </div>
  );
}

const AcceptInvite: React.FC = () => {
  const { t } = useTranslation();
  const { token } = useParams<{ token: string }>();
  const navigate = useNavigate();
  const {
    state: { isAuthenticated, isFetched, user },
  } = AuthModule.Context.useAuthContext();
  const [inviteInfo, setInviteInfo] = useState<InviteInfo | null>(null);
  const [loading, setLoading] = useState(true);
  // `error`: the invite itself is invalid/expired (set only by the initial
  // load) — renders the dedicated "expired" screen below. `actionError`: the
  // accept/decline call failed (e.g. a transient network error) — this must
  // NOT masquerade as an expired invite, so it renders inline on the current
  // screen with the real message and the same retry controls still visible.
  const [error, setError] = useState<string | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);
  const [accepting, setAccepting] = useState(false);
  const [declining, setDeclining] = useState(false);
  const [accepted, setAccepted] = useState(false);
  const [declined, setDeclined] = useState(false);

  useEffect(() => {
    if (!token) return;
    setLoading(true);
    Organization.Api.ValidateInvitation(token)
      .then(({ data }) => {
        setInviteInfo(data);
        setError(null);
      })
      .catch((err) => {
        setError(err?.response?.data?.message || 'This invitation is invalid or has expired.');
      })
      .finally(() => setLoading(false));
  }, [token]);

  const handleAccept = async () => {
    if (!token) return;
    setActionError(null);
    setAccepting(true);
    try {
      await Organization.Api.AcceptInvitation(token);
      setAccepted(true);
      setTimeout(() => navigate('/app'), 1500);
    } catch (err: unknown) {
      const e = err as { response?: { data?: { message?: string } } };
      // A failed accept (e.g. a transient network error) is not the same as
      // an expired invite — surface it inline and let the user retry instead
      // of falling through to the generic "invalid or expired" screen.
      setActionError(e?.response?.data?.message || t('invite.accept_failed', 'Failed to accept invitation. Please try again.'));
    } finally {
      setAccepting(false);
    }
  };

  const handleDecline = async () => {
    if (!token) return;
    setActionError(null);
    setDeclining(true);
    try {
      await Organization.Api.DeclineInvitation(token);
      setDeclined(true);
    } catch (err: unknown) {
      const e = err as { response?: { data?: { message?: string } } };
      setActionError(e?.response?.data?.message || t('invite.decline_failed', 'Failed to decline invitation. Please try again.'));
    } finally {
      setDeclining(false);
    }
  };

  const handleLoginRedirect = () => {
    if (token) sessionStorage.setItem('pending_invite_token', token);
    navigate('/login', {
      state: { from: { pathname: `/invite/${token}` }, email: inviteInfo?.email },
    });
  };

  const handleSignupRedirect = () => {
    if (token) sessionStorage.setItem('pending_invite_token', token);
    navigate('/signup', {
      state: { from: { pathname: `/invite/${token}` }, email: inviteInfo?.email, inviteToken: token },
    });
  };

  const org = inviteInfo?.organization.name ?? '';
  const role = roleLabel(inviteInfo?.role);

  if (loading) {
    return (
      <InviteShell>
        <div className="flex flex-col items-center gap-4 py-6">
          <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" aria-hidden />
          <p className="text-sm text-muted-foreground">{t('validating_invitation')}</p>
        </div>
      </InviteShell>
    );
  }

  if (declined) {
    return (
      <InviteShell>
        <IconTile Icon={XCircle} tone="text-muted-foreground" />
        <h1 className="text-xl font-bold tracking-tight">{t('invite.declined_title')}</h1>
        <p className="mt-2 text-sm text-muted-foreground">{t('invite.declined_desc')}</p>
        <Button asChild variant="outline" className="mt-6 w-full">
          <Link to={isAuthenticated ? '/app' : '/login'}>{t('invite.go_to_sign_in')}</Link>
        </Button>
      </InviteShell>
    );
  }

  if (error) {
    return (
      <InviteShell>
        <IconTile Icon={XCircle} tone="text-destructive" />
        <h1 className="text-xl font-bold tracking-tight">{t('invite.expired_title')}</h1>
        <p className="mt-2 text-sm text-muted-foreground">{t('invite.expired_desc')}</p>
        <Button asChild variant="outline" className="mt-6 w-full">
          <Link to="/login">{t('invite.go_to_sign_in')}</Link>
        </Button>
        <p className="mt-4 text-sm text-muted-foreground">
          {t('invite.have_a_code')}{' '}
          <Link to="/join" className="font-semibold text-foreground underline underline-offset-[3px] hover:decoration-2">
            {t('invite.enter_it_here')}
          </Link>
        </p>
      </InviteShell>
    );
  }

  if (accepted) {
    return (
      <InviteShell>
        <IconTile Icon={CheckCircle2} tone="text-success" />
        <h1 className="text-xl font-bold tracking-tight">{t('invite.youre_in')}</h1>
        <p className="mt-2 text-sm text-muted-foreground">{t('invite.joined_as', { org, role })}</p>
        <div className="mt-5 flex items-center justify-center gap-2 text-sm text-muted-foreground">
          <Loader2 className="h-4 w-4 animate-spin" aria-hidden />
          {t('invite.taking_you')}
        </div>
      </InviteShell>
    );
  }

  // Valid invite, authenticated → one-tap accept.
  if (isAuthenticated && isFetched) {
    return (
      <InviteShell
        avatar={
          <span className="flex h-8 w-8 items-center justify-center rounded-full bg-primary text-xs font-bold text-primary-foreground">
            {initials(user?.name)}
          </span>
        }
      >
        <IconTile Icon={Briefcase} tone="text-foreground" />
        <h1 className="text-xl font-bold tracking-tight">{t('invite.join_org', { org })}</h1>
        <p className="mt-2 text-sm text-muted-foreground">
          {t('invite.signed_in_as', { name: user?.name ?? '', role })}
        </p>
        {actionError && (
          <div className="mt-4 flex items-center gap-2 rounded-lg border border-destructive/30 bg-destructive/10 px-3 py-2 text-left text-sm text-destructive">
            <AlertCircle className="h-4 w-4 shrink-0" aria-hidden />
            <span>{actionError}</span>
          </div>
        )}
        <Button onClick={handleAccept} disabled={accepting || declining} className="mt-6 w-full">
          {accepting ? (
            <Loader2 className="mr-2 h-4 w-4 animate-spin" />
          ) : (
            <CheckCircle2 className="mr-2 h-4 w-4" />
          )}
          {t('invite.accept')}
        </Button>
        <Button
          onClick={handleDecline}
          disabled={accepting || declining}
          variant="outline"
          className="mt-3 w-full"
        >
          {declining && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
          {t('decline')}
        </Button>
      </InviteShell>
    );
  }

  // Valid invite, existing account, signed out → sign in to accept.
  if (inviteInfo?.userExists) {
    return (
      <InviteShell>
        <IconTile Icon={LogIn} tone="text-foreground" />
        <h1 className="text-xl font-bold tracking-tight">{t('invite.sign_in_title')}</h1>
        <p className="mt-2 text-sm text-muted-foreground">{t('invite.existing_desc', { org, role })}</p>
        {inviteInfo.email ? (
          <div className="mt-5 flex items-center gap-2 rounded-lg border border-border bg-muted/40 px-4 py-3 text-left text-sm text-muted-foreground">
            <Mail className="h-4 w-4 shrink-0" aria-hidden />
            <span className="truncate">{inviteInfo.email}</span>
          </div>
        ) : null}
        <Button onClick={handleLoginRedirect} className="mt-5 w-full">
          <LogIn className="mr-2 h-4 w-4" />
          {t('invite.sign_in_to_accept')}
        </Button>
      </InviteShell>
    );
  }

  // Valid invite, brand-new user → landing → signup wizard.
  return (
    <InviteShell>
      <IconTile Icon={Briefcase} tone="text-foreground" />
      <h1 className="text-xl font-bold tracking-tight">{t('invite.youre_invited')}</h1>
      <p className="mt-2 text-sm text-muted-foreground">
        <span className="font-semibold text-foreground">{org}</span> {t('invite.invited_to_join_suffix')}{' '}
        <span className="inline-block rounded-md bg-muted px-2 py-0.5 text-xs font-semibold text-foreground">
          {role}
        </span>
      </p>
      {inviteInfo?.email ? (
        <div className="mt-4 flex items-center gap-2 rounded-lg border border-border bg-muted/40 px-4 py-3 text-left text-sm text-muted-foreground">
          <Mail className="h-4 w-4 shrink-0" aria-hidden />
          <span className="truncate">
            {t('invite.invited_label')} {inviteInfo.email}
          </span>
        </div>
      ) : null}
      <Button onClick={handleSignupRedirect} className="group mt-5 w-full">
        {t('invite.create_your_account')}
        <ArrowRight className="ml-2 h-4 w-4 transition-transform group-hover:translate-x-0.5" />
      </Button>
      <p className="mt-4 text-sm text-muted-foreground">
        {t('already_have_an_account')}{' '}
        <button
          type="button"
          onClick={handleLoginRedirect}
          className="font-semibold text-foreground underline underline-offset-[3px] hover:decoration-2"
        >
          {t('sign_in')}
        </button>
      </p>
    </InviteShell>
  );
};

export default AcceptInvite;
